/** Stop expecting a stream — **F-006**, and the control `useRetireSource` was waiting for.
 *
 * `Sources.tsx` called the hook and printed *"Retiring…"* from `retire.isPending`, and **nothing ever
 * called `retire.mutate`**: a spinner for an act no control could start. Meanwhile the row's own
 * explanation of a derived end of life says, for the stream's own retirement, *"the stream's own
 * dates are what to change"* — a remedy with no way to carry it out.
 *
 * ## What is being retired, which was the open question
 *
 * A source's life is **derived from three rows** — the declaration's dates, the meter's and the
 * site's (`census.life_of`) — and each is a different act with a different home:
 *
 * - the **site** closing and the **meter** coming out are the Organisation screen's (`retire_facility`,
 *   `retire_meter`, both Admin-only); the rows on this screen say so under *"Nothing expected
 *   after …"* and name that screen as the remedy;
 * - the **stream** — the declaration of *"we expect a gas bill each month at this site"* — is this
 *   route's, `POST /sources/{id}/retire`, and it writes `expected_to` on that one row and nothing
 *   else.
 *
 * So the question has an answer in the API and not a guess here: this control retires the
 * **declaration**, says so, and says where the other two live. It does not touch the meter or the
 * site, and a person who closed a site does not need it (the site's end already narrows the life).
 *
 * ## Four properties worth keeping
 *
 * - **The date is the last day it was expected**, not the first day it is not. A meter replaced on
 *   1 April is retired on 31 March and the March bill is still expected; off by one is either a
 *   missing month that cannot exist or a phantom one (`service.retire`). Said beside the field,
 *   as `MeterRetirement` does.
 * - **There is no un-retire.** The route can be asked again with a *different* date, which is how a
 *   mistake is moved, and the control stays on a retired stream for that reason. A reader is told
 *   it cannot be taken back, because the ledger has no undo and a form that implied one would be
 *   the thing that was untrue.
 * - **Asking twice with the same date and reason writes nothing** (ADR-0047), so a retry after an
 *   unknown outcome is safe and `WriteFailed` is told so.
 * - **Nothing is deleted.** Every window already judged stays judged, and every filed figure is
 *   unchanged; what stops is the census counting later windows as missing.
 */

import type { ReactNode } from "react";
import { useState } from "react";

import type { SourceCoverage } from "../api/sources";
import { useRetireSource } from "../api/sources";
import { isPeriodClosed } from "../api/refusal";
import { WriteFailed } from "../components/Loaded";
import { PostCloseRetry } from "./PeriodLock";
import { shortDate } from "../lib/format";
import styles from "./Screen.module.css";
import own from "./Sources.module.css";
import reg from "./Register.module.css";

export function SourceRetirement({
  source,
  name,
  from,
  to,
  mayRetire,
  onReload,
  children,
}: {
  source: SourceCoverage;
  /** What the stream is called on the row, so the accessible names agree with what is printed. */
  name: string;
  /** The census window on screen — only to invalidate the right cache entry afterwards. */
  from: string;
  to: string;
  /** `undefined` while the session is still loading. Not the same as "may not": the control is
   * withheld rather than the refusal sentence flashed (`Bulk.tsx`). */
  mayRetire: boolean | undefined;
  /** Ask the census again — the remedy for a stale or unknown-outcome refusal. */
  onReload: () => void;
  /** The row's other actions, drawn in the same column as this one's trigger. They are passed in
   * rather than rendered beside it because this component returns nothing at all for a role that
   * may not retire, and a row's other controls must not vanish with it. */
  children?: ReactNode;
}) {
  const retire = useRetireSource(from, to);
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(source.expected_to ?? "");
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  /** What the last success said, held here because the form closes on it. Cleared when the form
   * is opened again, so a stale confirmation never sits above a fresh attempt. */
  const [done, setDone] = useState<string | null>(null);

  // Not offered at all: the sentence is the panel's, said once (`retire-who-can`), because one per
  // row is a wall of the same paragraph on a screen that already has a lot of rows.
  if (mayRetire !== true) {
    return children ? <div className={own.rowActions}>{children}</div> : null;
  }

  const retired = source.is_retired;

  function close() {
    setOpen(false);
    setWhen(source.expected_to ?? "");
    setReason("");
    setRefusal(null);
  }

  async function submit() {
    setRefusal(null);
    try {
      await retire.mutateAsync({ sourceId: source.source_id, effective_from: when, reason });
      setDone(
        `${name} is not expected after ${shortDate(when)}. Nothing was deleted and every figure that cites it is unchanged.`,
      );
      setOpen(false);
      setReason("");
    } catch (error) {
      // **Kept open**, with the date and reason still in the boxes: a refusal such as a date before
      // the stream began is corrected, not retyped from nothing.
      setRefusal(error);
    }
  }

  return (
    <>
      <div className={own.rowActions}>
        {children}
        <button
          type="button"
          className={reg.act}
          data-testid="retire-source-open"
          data-source-id={source.source_id}
          aria-expanded={open}
          aria-label={`${retired ? "Change when" : "Stop expecting"} ${name}${retired ? " stopped being expected" : ""}`}
          onClick={() => {
            if (open) {
              close();
              return;
            }
            setDone(null);
            setOpen(true);
          }}
        >
          {open ? "Not now" : retired ? "Change when it stopped" : "Stop expecting this"}
        </button>
      </div>

      {done ? (
        <p className={own.formNote} role="status" data-testid="source-retired">
          {done}
        </p>
      ) : null}

      {open ? (
        <form
          className={own.form}
          data-testid="retire-source-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <p className={own.formHead}>
            {retired ? "Move" : "Stop"} this stream&rsquo;s expectation — not the meter, not the
            site
          </p>
          <label className={own.field}>
            <span className={own.fieldLabel}>Last day it was expected</span>
            <input
              className={own.input}
              type="date"
              required
              value={when}
              min={source.expected_from}
              aria-label={`Last day ${name} was expected`}
              onChange={(event) => setWhen(event.target.value)}
            />
          </label>
          <p className={own.formNote}>
            A supply replaced on 1 April ends on 31 March — the March bill is still expected. A date
            before this stream began ({shortDate(source.expected_from)}) is refused.
          </p>
          <label className={own.field}>
            <span className={own.fieldLabel}>Why</span>
            <input
              className={own.input}
              required
              value={reason}
              aria-label={`Why ${name} stops being expected`}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. the contract ended and the site now buys on another account"
            />
          </label>
          <p className={own.formNote}>
            Nothing is deleted: periods already judged stay judged and no filed figure moves. The
            census only stops counting later periods as missing. <strong>This cannot be taken
            back</strong> — only moved to a different date, by opening this again. If a{" "}
            <strong>meter</strong> came out or a <strong>site</strong> closed, that is recorded on
            Organisation, and this row will then say which of the three ended it.
          </p>
          <div className={own.buttons}>
            <button
              type="submit"
              className={styles.primary}
              disabled={!when || !reason.trim() || retire.isPending}
            >
              {retire.isPending ? "Recording…" : retired ? "Move the date" : "Stop expecting it"}
            </button>
            <button type="button" className={styles.secondary} onClick={close}>
              Cancel
            </button>
          </div>
          {/* Back-dated into a closed period (ADR-0094). The reason above is already the Admin's
              act, so this only tells everyone else whose it is - and the "try again" below is
              withheld, because sending it again would be refused again. */}
          <PostCloseRetry error={refusal} />
          <WriteFailed
            error={isPeriodClosed(refusal) ? null : refusal}
            of="this source"
            testId="retire-source-refused"
            // Safe, and said: the same date with the same reason writes nothing (ADR-0047).
            retrySafe
            onRetry={() => void submit()}
            onReload={() => {
              onReload();
              close();
            }}
          />
        </form>
      ) : null}
    </>
  );
}
